import { particles } from 'app/contracts/Particles';
import { ParticleProperties } from './ParticleProperties';
import { ParticleBehaviourManager } from './ParticleBehaviourManager';
import { ParticleTint } from './behaviour/ParticleTint';
import { ParticleTexture } from './behaviour/ParticleTexture';
import { ParticlePosition } from './behaviour/ParticlePosition';
import ParticleBehaviourLookup from './ParticleBehaviourLookup';

export class Particle implements particles.Particle {
	public lookupIndex: number = -1;
	public _active: boolean = true;

	//Prop objects:
	protected _sceneProps: particles.SceneProps;
	protected _particleProps: particles.ParticleProps;
	protected _animatedProps: particles.ParticleProperties;

	//Behaviours:
	protected _behaviourManager: ParticleBehaviourManager;
	protected _tint: ParticleTint;
	protected _texture: ParticleTexture;
	protected _position: particles.ParticlePosition;
	protected _randoms: any;
	protected _tags: Array<string> = [];

	constructor(sceneProps: particles.SceneProps, container: PIXI.Container, config: particles.ParticleConfig) {
		this._sceneProps = sceneProps;
		this._particleProps = { id: config.base.id, container: container, sprite: null, ref: this };
		this._createSprite(config.base.z);
		this._createAnimatedProps(config.ease);

		this._behaviourManager = new ParticleBehaviourManager(this._sceneProps, this._particleProps, this._animatedProps);
		this._createBaseBehaviours(config.base);
		this.createBehaviours(config);
		this._setIntialState(config);

		this._active = false;
		this._particleProps.sprite.visible = false;
	}

	//GETTER / SETTER
	//--------------------------------------------------------------------------------------------------
	public get active(): boolean {
		return this._active;
	}

	public set active(value: boolean) {
		ParticleBehaviourLookup.setActiveParticle(this.lookupIndex, value);

		this._active = value;
		this._particleProps.sprite.visible = value;
	}

	public setActive(value: boolean) {
		if (this._active != value) {
			ParticleBehaviourLookup.setActiveParticle(this.lookupIndex, value);
		}

		this._active = value;

		this._particleProps.sprite.visible = value;
	}

	public get id(): string {
		return this._particleProps.id;
	}

	public get position(): particles.ParticlePosition {
		return this._position;
	}

	public get colour(): particles.ParticleColour {
		return this._tint;
	}

	public get particleProps(): particles.ParticleProps {
		return this._particleProps;
	}

	public get animatedProps(): particles.ParticleProperties {
		return this._animatedProps;
	}

	//PUBLIC METHODS
	//--------------------------------------------------------------------------------------------------
	public getLookupIndex(): number {
		if (!this.lookupIndex || this.lookupIndex === -1) {
			this.lookupIndex = ParticleBehaviourLookup.getParticleNextLookupIndex();
		}
		return this.lookupIndex;
	}

	public tick(): void {
		if (this._active) {
			//Update behaviours:
			this._behaviourManager.tick();
			//Update based on new target positions/alpha/scale/rotation
			this._animatedProps.tick();
		}
	}

	public resize(): void {
		this._behaviourManager.resize();
	}

	public destroy(): void {
		this._active = false;
		this._behaviourManager.destroy();
		this._tint = this._texture = this._particleProps.sprite = null;
		this._sceneProps = this._particleProps = this._animatedProps = null;
	}

	public addTag(tag: string): void {
		this._tags.push(tag);
	}

	public removeTag(tag: string): void {
		const index = this._tags.indexOf(tag);
		if (index > -1) {
			this._tags.splice(index, 1);
		}
	}

	public hasTag(tag: string): boolean {
		return this._tags.includes(tag);
	}

	public removeAllTags(): void {
		this._tags = [];
	}

	public match(particle: particles.Particle): void {
		this.position.set({ x: particle.position.x, y: particle.position.y }, true);
		this.animatedProps.targetScale = this.animatedProps.scale = particle.animatedProps.scale;
		this.animatedProps.targetAlpha = this.animatedProps.alpha = particle.animatedProps.alpha;
	}

	public setVisible(visible: boolean): void {
		this.particleProps.sprite.visible = visible;
	}

	//PROTECTED (OVERIDABLE) METHODS
	//--------------------------------------------------------------------------------------------------
	protected createBehaviours(config: particles.ParticleConfig): void {
		this._position = this._behaviourManager.add(ParticlePosition);
	}

	public deactivateAllBehaviours(baseActive: boolean = false): void {
		this._behaviourManager.deactiveAll();

		//If base is active then
		if (baseActive) {
			this.position.active = true;
		}
	}

	//PRIVATE METHODS
	//--------------------------------------------------------------------------------------------------
	private _createSprite(z: number = 1): void {
		this._particleProps.sprite = new PIXI.Sprite();
		this._particleProps.sprite.interactive = false;
		this._particleProps.sprite.cullable = true;
		this._particleProps.sprite.anchor.set(0.5, 0.5);
		(this._particleProps.sprite as any).z = z;

		this._particleProps.container.addChild(this._particleProps.sprite);
	}

	private _createAnimatedProps(easeConfig: particles.ParticleEaseConfig): void {
		//Handles ineria on the props:
		//Must be created before any behavours as its passed in their constructor:
		this._animatedProps = new ParticleProperties(this._particleProps.sprite);
		this._animatedProps.setEase(easeConfig);
	}

	private _createBaseBehaviours(baseconfig: particles.ParticleBaseConfig): void {
		//Add the tint:
		this._tint = this._behaviourManager.add(ParticleTint);
		if (baseconfig.tint) {
			this._tint.set(baseconfig.tint);
		}

		//Add the texture:
		this._texture = this._behaviourManager.add(ParticleTexture);
		this._texture.set(baseconfig.texture);
	}

	private _setIntialState(config: particles.ParticleConfig): void {
		if (!config.intialState) {
			config.intialState = { x: 0, y: 0 };
		}

		this._animatedProps.setState(config.intialState);

		//Set on any acting behaviours:
		this._position.set({ x: this._animatedProps.x, y: this._animatedProps.y });
	}
}
